import { LogLevel, PublicClientApplication, type Configuration } from "@azure/msal-browser";
import type { AppConfig } from "./authConfig";

/**
 * Construct a `PublicClientApplication` configured for Authorization Code +
 * PKCE redirect flow.
 *
 * Construction and initialization are intentionally separate: MSAL requires
 * `await initialize()` (and `handleRedirectPromise()`) before any token API
 * call, but that lifecycle belongs to `main.tsx`. Keeping construction pure
 * makes this helper trivial to unit-test without mocking initialization
 * promises.
 */
export function createMsalInstance(config: AppConfig): PublicClientApplication {
    // `import.meta.env.BASE_URL` resolves to the Vite base (`/` for local and
    // deployed builds). Deriving the URI from the current origin keeps QA and
    // production on their respective Office subdomains.
    const redirectUri = `${window.location.origin}${import.meta.env.BASE_URL}`;

    const configuration: Configuration = {
        auth: {
            clientId: config.msal.clientId,
            authority: config.msal.authority,
            redirectUri,
            postLogoutRedirectUri: redirectUri,
            navigateToLoginRequestUrl: true,
        },
        cache: {
            // sessionStorage over localStorage: tokens are not persisted
            // beyond the tab lifetime and are not shared across origin
            // tabs, which meaningfully reduces token-theft blast radius.
            cacheLocation: "sessionStorage",
            storeAuthStateInCookie: false,
        },
        system: {
            loggerOptions: {
                logLevel: LogLevel.Warning,
                piiLoggingEnabled: false,
                loggerCallback: (level, message) => {
                    if (level === LogLevel.Error) {
                        console.error(`[msal] ${message}`);
                    } else if (level === LogLevel.Warning) {
                        console.warn(`[msal] ${message}`);
                    }
                },
            },
        },
    };

    return new PublicClientApplication(configuration);
}
